import React, { useCallback, useState } from 'react';

import { componentTypes } from '@white-nl/instantsearch-components';
import DOMPurify from 'dompurify';
import Picture from '../../../ui/Picture';
import {
  ArrowRightSharpRegular,
  EnvelopeSharpRegular,
  FaxSharpRegular,
  GlobeSharpRegular,
  MobileSharpRegular,
  PhoneSharpRegular,
} from '../../../ui/icons';

declare global {
  interface Window {
    bootstrap: {
      Modal: {
        getOrCreateInstance: (element: Element) => { show: () => void };
      };
    };
    htmx: {
      trigger: (id: string, action: string) => void;
      on: (event: string, callback: () => void) => void;
    };
  }
}

export default function Hit({ hit, additionalData }: componentTypes.HitProps) {
  const {
    title,
    logo,
    address,
    phone,
    mobile,
    fax,
    email,
    website,
    link,
    datacenter,
    premium,
    functionDescription,
  } = hit;

  const { formHandle } = additionalData;

  const hasIcons = datacenter || premium;

  const websiteWithProtocol = website && !website.startsWith('http')
    ? `https://${website}` : website;

  const [lastTimeClicked, setLastTimeClicked] = useState(0);

  const handleContactClick = useCallback(() => {
    const currentTime = new Date().getTime();
    if ((currentTime - lastTimeClicked) < 2000) {
      return;
    }
    setLastTimeClicked(currentTime);

    const selector = `#form-modal-sprig-${formHandle}-0`;
    const element = document.querySelector(selector);

    if (!element) {
      return;
    }

    const divElement = element as HTMLDivElement;
    const data = JSON.parse(divElement.dataset.hxVals || '');
    const newData = {
      ...data,
      ...{
        recipient: email,
        largeForm: true,
        pageUrl: window.location.href,
        textModal: `<div class='h5'>${title}</div><p class='mb-4'>${Craft.t('contact', 'Fill in the form below so we can help you out.')}</p>`,
        emailSubject: title,
      },
    };
    divElement.dataset.hxVals = JSON.stringify(newData);
    window.htmx.trigger(selector, 'refresh');
  }, [formHandle, title, email, lastTimeClicked]);

  return (
    <div className="col-12 col-md-6 col-xl-4">
      <div className="card card-partners">
        {hasIcons && (
          <div className="card-partners-icons">
            {datacenter && (<div className="btn-datacenter" />)}
            {premium && (<div className="btn-premium" />)}
          </div>
        )}
        {logo && (
          logo.svg
            ? (
              <div
                className="card-img-top ratio ratio-3x2"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(logo.svg) }}
              />
            ) : (
              <div className="card-img-top ratio ratio-3x2">
                <Picture image={logo} defaultTransform="xxl-min" loading="lazy" />
              </div>
            )
        )}
        {!logo && (
          <div className="card-img-top ratio ratio-3x2">
            <span />
          </div>
        )}
        <div className="card-body">
          <div>
            <h2 className="card-title">{title}</h2>
            {functionDescription && (
              <p className="function mb-2">{functionDescription}</p>
            )}
            {address && (
              <address
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(address) }}
              />
            )}
          </div>
          <ul className={link ? 'with-border' : 'without-border'}>
            {phone && (
              <li>
                <a href={`tel:${phone}`} target="_blank" rel="noreferrer noopener">
                  <PhoneSharpRegular />
                  {phone}
                </a>
              </li>
            )}
            {mobile && (
              <li>
                <a href={`tel:${mobile}`} target="_blank" rel="noreferrer noopener">
                  <MobileSharpRegular />
                  {mobile}
                </a>
              </li>
            )}
            {fax && (
              <li>
                <FaxSharpRegular />
                {fax}
              </li>
            )}
            {email && (
              <li>
                <button type="button" onClick={handleContactClick}>
                  <EnvelopeSharpRegular />
                  {Craft.t('partners', 'Contact by mail')}
                </button>
              </li>
            )}
            {websiteWithProtocol && (
              <li>
                <a href={websiteWithProtocol} target="_blank" rel="noreferrer noopener">
                  <GlobeSharpRegular />
                  {Craft.t('partners', 'Visit website')}
                </a>
              </li>
            )}
          </ul>
        </div>
        {link && (
          <div className="card-footer">
            <a href={link} className="more-link-sm">
              {Craft.t('partners', 'Explore partnership benefits')}
              <ArrowRightSharpRegular />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
